import type { AppUser, ProfileData } from '@/types/supabase';

import { sanitizeRedirectPath } from '@/utils/auth/sanitizeRedirect';

type CompletionProfile = Pick<ProfileData, 'first_name' | 'last_name' | 'whatsapp_number'> | null;

function hasText(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

function getIdentityProviders(user: Pick<AppUser, 'identities'> | null | undefined): string[] {
  if (!user || !Array.isArray(user.identities)) {
    return [];
  }

  return user.identities
    .map((identity) => identity?.provider)
    .filter((provider): provider is string => typeof provider === 'string');
}

export function isWhatsAppAuthUser(user: Pick<AppUser, 'app_metadata' | 'identities' | 'phone'> | null | undefined): boolean {
  if (!user) {
    return false;
  }

  const provider = typeof user.app_metadata?.provider === 'string' ? user.app_metadata.provider : null;
  if (provider === 'phone') {
    return true;
  }

  const identityProviders = getIdentityProviders(user);
  if (identityProviders.includes('phone')) {
    return true;
  }

  return Boolean(user.phone && !provider && identityProviders.length === 0);
}

export function isGoogleAuthUser(user: Pick<AppUser, 'app_metadata' | 'identities'> | null | undefined): boolean {
  if (!user) {
    return false;
  }

  const provider = typeof user.app_metadata?.provider === 'string' ? user.app_metadata.provider : null;
  return provider === 'google' || getIdentityProviders(user).includes('google');
}

export function needsProfileCompletion(user: AppUser | null | undefined, profile: CompletionProfile): boolean {
  if (!user) {
    return false;
  }

  const missingName = !hasText(profile?.first_name) || !hasText(profile?.last_name);
  if (isWhatsAppAuthUser(user)) {
    return missingName || !hasText(user.email);
  }

  if (isGoogleAuthUser(user)) {
    return missingName || !hasText(profile?.whatsapp_number);
  }

  return false;
}

export function buildProfileCompletionRedirect(redirectPath?: string | null): string {
  const params = new URLSearchParams({ complete: 'true' });
  const safeRedirectPath = sanitizeRedirectPath(redirectPath);

  if (safeRedirectPath !== '/') {
    params.set('redirect', safeRedirectPath);
  }

  return `/profile?${params.toString()}`;
}